import { liveQuery, type Subscription } from 'dexie';
import type { z } from 'zod';
import { isTerminalJobStatus, type Job } from '@/entities/job';
import type { ReplicateClient } from '@/shared/api/replicate';
import { getDB, type BrandGeneratorDB } from '@/shared/storage';
import { RUNNERS, type RunnerContext, type RunnerRegistry } from './runners';
import { recomputeComposite } from './rollup';
import { useSchedulerState } from './store';

export interface SchedulerContextInput {
  client: ReplicateClient;
  concurrencyCap: number;
}

class JobScheduler {
  private ctx: SchedulerContextInput | null = null;
  private running = new Map<string, AbortController>();
  private subscription: Subscription | null = null;
  private ticking = false;
  private dirty = false;
  private runners: RunnerRegistry = RUNNERS;

  /** Test seam — override the runner registry. */
  setRunners(runners: RunnerRegistry): void {
    this.runners = runners;
  }

  /** Test seam — wait for all currently dispatched jobs to settle. */
  async drain(): Promise<void> {
    while (this.running.size > 0 || this.ticking) {
      await new Promise((r) => setTimeout(r, 0));
    }
  }

  start(): void {
    if (this.subscription) return;
    this.subscription = liveQuery(() =>
      getDB().jobs.where('status').anyOf(['pending', 'running']).count(),
    ).subscribe({
      next: () => {
        void this.tick();
      },
      error: (err: unknown) => {
        console.error('Scheduler liveQuery error', err);
      },
    });
  }

  stop(): void {
    this.subscription?.unsubscribe();
    this.subscription = null;
  }

  setContext(ctx: SchedulerContextInput | null): void {
    this.ctx = ctx;
    void this.tick();
  }

  /** For tests. */
  hasContext(): boolean {
    return this.ctx !== null;
  }

  async cancel(jobId: string): Promise<void> {
    const db = getDB();
    const subtree = await collectSubtree(jobId, db);
    for (const j of subtree) {
      const ac = this.running.get(j.id);
      if (ac) ac.abort();
      if (!isTerminalJobStatus(j.status)) {
        await db.jobs.update(j.id, { status: 'canceled', completedAt: Date.now() });
      }
    }
    const root = await db.jobs.get(jobId);
    if (root?.parentJobId) await recomputeComposite(root.parentJobId, db);
    this.publishRunning();
    void this.tick();
  }

  /**
   * Re-arms a finished/interrupted job (or every non-succeeded child of a
   * composite) for the scheduler to pick up again.
   */
  async retry(jobId: string): Promise<void> {
    const db = getDB();
    const job = await db.jobs.get(jobId);
    if (!job) return;

    if (job.kind === 'composite') {
      const children = await db.jobs.where('parentJobId').equals(jobId).toArray();
      for (const c of children) {
        if (c.status !== 'succeeded') await this.retry(c.id);
      }
      await db.jobs.update(jobId, {
        status: 'pending',
        error: undefined,
        completedAt: undefined,
      });
    } else {
      if (job.status === 'succeeded' || job.status === 'running') return;
      await db.jobs.update(jobId, {
        status: 'pending',
        error: undefined,
        startedAt: undefined,
        completedAt: undefined,
        output: undefined,
        replicateId: undefined,
        actualCostUsd: 0,
        usage: {},
      });
    }
    if (job.parentJobId) await recomputeComposite(job.parentJobId, db);
    void this.tick();
  }

  async tick(): Promise<void> {
    if (this.ticking) {
      this.dirty = true;
      return;
    }
    this.ticking = true;
    try {
      do {
        this.dirty = false;
        await this.tickOnce();
      } while (this.dirty);
    } finally {
      this.ticking = false;
    }
  }

  private async tickOnce(): Promise<void> {
    const ctx = this.ctx;
    if (!ctx) return;
    const db = getDB();
    const pending = await db.jobs.where('status').equals('pending').sortBy('createdAt');

    for (const job of pending) {
      if (job.kind === 'composite') continue;
      if (this.running.has(job.id)) continue;
      if (this.running.size >= ctx.concurrencyCap) break;

      if (job.dependsOn.length > 0) {
        const deps = await db.jobs.bulkGet(job.dependsOn);
        const anyDead = deps.some(
          (d) => !d || d.status === 'failed' || d.status === 'canceled',
        );
        if (anyDead) {
          await db.jobs.update(job.id, {
            status: 'canceled',
            error: 'Dependency did not succeed.',
            completedAt: Date.now(),
          });
          if (job.parentJobId) await recomputeComposite(job.parentJobId, db);
          this.dirty = true;
          continue;
        }
        const allOk = deps.every((d) => d?.status === 'succeeded');
        if (!allOk) continue;
      }

      const claimed = await db.transaction('rw', db.jobs, async () => {
        const fresh = await db.jobs.get(job.id);
        if (!fresh || fresh.status !== 'pending') return false;
        await db.jobs.update(job.id, { status: 'running', startedAt: Date.now() });
        return true;
      });
      if (!claimed) continue;

      this.dispatch(job, ctx.client);
    }
  }

  private dispatch(job: Job, client: ReplicateClient): void {
    const ac = new AbortController();
    this.running.set(job.id, ac);
    this.publishRunning();

    void this.runOne(job, client, ac.signal).finally(() => {
      this.running.delete(job.id);
      this.publishRunning();
      void this.tick();
    });
  }

  private async runOne(job: Job, client: ReplicateClient, signal: AbortSignal): Promise<void> {
    const db = getDB();
    const ctx: RunnerContext = {
      client,
      signal,
      db,
      getJobOutput: makeGetJobOutput(db),
    };
    try {
      const runner = this.runners[job.kind];
      const result = await runner(job, ctx);
      const patch: Partial<Job> = {
        status: 'succeeded',
        output: result.output,
        usage: result.usage ?? {},
        actualCostUsd: result.actualCostUsd,
        completedAt: Date.now(),
      };
      if (result.replicateId !== undefined) patch.replicateId = result.replicateId;
      await db.jobs.update(job.id, patch);
    } catch (err) {
      const aborted = signal.aborted || (err as Error)?.name === 'AbortError';
      const message = err instanceof Error ? err.message : 'Unknown error';
      await db.jobs.update(job.id, {
        status: aborted ? 'canceled' : 'failed',
        error: aborted ? 'Canceled by user.' : message,
        completedAt: Date.now(),
      });
    } finally {
      const fresh = await db.jobs.get(job.id);
      if (fresh?.parentJobId) await recomputeComposite(fresh.parentJobId, db);
      // If we failed, dependent leaves will be cascaded to canceled by tick().
    }
  }

  private publishRunning(): void {
    useSchedulerState.getState().setRunning(new Set(this.running.keys()));
  }
}

function makeGetJobOutput(db: BrandGeneratorDB) {
  return async <T>(jobId: string, schema: z.ZodType<T>): Promise<T> => {
    const job = await db.jobs.get(jobId);
    if (!job) throw new Error(`Dependency job ${jobId} not found.`);
    if (job.status !== 'succeeded') {
      throw new Error(`Dependency job ${jobId} did not succeed (status=${job.status}).`);
    }
    return schema.parse(job.output);
  };
}

async function collectSubtree(rootId: string, db: BrandGeneratorDB): Promise<Job[]> {
  const out: Job[] = [];
  const queue = [rootId];
  while (queue.length > 0) {
    const id = queue.shift();
    if (id === undefined) break;
    const job = await db.jobs.get(id);
    if (!job) continue;
    out.push(job);
    const children = await db.jobs.where('parentJobId').equals(id).toArray();
    queue.push(...children.map((c) => c.id));
  }
  return out;
}

export const jobScheduler = new JobScheduler();
